import config from "../../config.json";

// LUD Copilot support conversations (backend: /api/auth/copilot, Phase 1).
//
// Two kinds of callers, never mixed in one request:
//   signed in  -> Authorization: Bearer <session token>
//   guest      -> X-Copilot-Guest-Token: <token of that one conversation>
// Pass `guestToken` for a guest call; leave it out to use the session.

const BASE = `${config.BASE_AUTH_API}copilot/`;
export const GUEST_TOKEN_HEADER = "X-Copilot-Guest-Token";

export class CopilotApiError extends Error {
  constructor(message, { status, code, retryAfter, network = false } = {}) {
    super(message);
    this.name = "CopilotApiError";
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
    this.network = network;
  }
}

const sessionToken = () => {
  try {
    return localStorage.getItem("auth-token");
  } catch {
    return null;
  }
};

export async function copilotRequest(path, { method = "GET", body, form, guestToken, idempotencyKey, signal, noAuth = false } = {}) {
  const token = guestToken || noAuth ? null : sessionToken();
  let response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      signal,
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(guestToken ? { [GUEST_TOKEN_HEADER]: guestToken } : {}),
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      ...(form ? { body: form } : body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    throw new CopilotApiError("Network error", { network: true });
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new CopilotApiError(data.message || data.error || "Request failed", {
      status: response.status,
      code: data.code,
      retryAfter: Number(response.headers.get("Retry-After")) || undefined,
    });
  }
  return data;
}

export const copilotQuery = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.append(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : "";
};

// Public endpoints (categories, FAQs) work with or without a session. A stale session token must
// not break them for a visitor, so a 401 retries once as an anonymous visitor.
export async function copilotPublicRequest(path, options = {}) {
  try {
    return await copilotRequest(path, options);
  } catch (error) {
    if (error?.status === 401 && !options.noAuth) return copilotRequest(path, { ...options, noAuth: true });
    throw error;
  }
}

export const fetchCopilotMetaAPI = (signal, { noAuth = false } = {}) => copilotPublicRequest("meta", { signal, noAuth });

// ---- FAQ ("Common questions") -----------------------------------------------------------
// The server decides what the caller may see; `noAuth` is for the signed-out widget.
// -> { categories: [{ value, labelEn, labelAr, count }], faqs: [{ key, category, order, question: { ar, en } }] }
export const fetchCopilotFaqsAPI = ({ signal, noAuth = false } = {}) => copilotPublicRequest("faqs", { signal, noAuth });
// -> { faq: { key, category, question, answer: { ar, en }, supportCategory } }
export const fetchCopilotFaqAPI = (key, { signal, noAuth = false } = {}) => copilotPublicRequest(`faqs/${encodeURIComponent(key)}`, { signal, noAuth });
// Anonymous visitors pass a random per-browser `sessionId` (no personal data).
export const sendCopilotFaqFeedbackAPI = (key, { helpful, sessionId }, { noAuth = false } = {}) =>
  copilotPublicRequest(`faqs/${encodeURIComponent(key)}/feedback`, { method: "POST", body: { helpful, ...(sessionId ? { sessionId } : {}) }, noAuth });

// Signed in: body = { category, subject, message, restaurant? }.
// Guest: body also needs { name, email, phone }; the answer carries
// `guestAccess.token` exactly once.
export const createCopilotConversationAPI = (body, { idempotencyKey, asGuest = false } = {}) =>
  copilotRequest("conversations", { method: "POST", body, idempotencyKey, noAuth: asGuest });

export const listCopilotConversationsAPI = ({ cursor, limit = 20, signal } = {}) =>
  copilotRequest(`conversations${copilotQuery({ cursor, limit })}`, { signal });

export const getCopilotConversationAPI = (id, { guestToken, before, limit = 30, signal } = {}) =>
  copilotRequest(`conversations/${encodeURIComponent(id)}${copilotQuery({ before, limit })}`, { guestToken, signal });

export const sendCopilotMessageAPI = (id, { text, attachments, clientMessageId }, { guestToken } = {}) =>
  copilotRequest(`conversations/${encodeURIComponent(id)}/messages`, {
    method: "POST",
    guestToken,
    idempotencyKey: clientMessageId,
    body: { ...(text ? { text } : {}), ...(attachments?.length ? { attachments } : {}) },
  });

export const markCopilotReadAPI = (id, { guestToken } = {}) =>
  copilotRequest(`conversations/${encodeURIComponent(id)}/read`, { method: "POST", guestToken, body: {} });

export const uploadCopilotAttachmentAPI = (id, file, { guestToken } = {}) => {
  const form = new FormData();
  form.append("file", file);
  return copilotRequest(`conversations/${encodeURIComponent(id)}/attachments`, { method: "POST", guestToken, form });
};

// Signed in: totals over all conversations. Guest: pass the conversation.
export const fetchCopilotUnreadAPI = ({ guestToken, conversation, signal } = {}) =>
  copilotRequest(`conversations/unread-count${copilotQuery({ conversation })}`, { guestToken, signal });

// ---- notifications & preferences (signed-in users) ---------------------------------
// In-app Copilot notifications are private to the user (the store-scoped
// /notification API never returns them).
// -> { notifications: [{ id, event, audience, conversationId, reference, title, titleEn, body, bodyEn, link, unread, createdAt }], unreadCount, hasMore, nextBefore }
export const fetchCopilotNotificationsAPI = ({ limit = 30, before, signal } = {}) =>
  copilotRequest(`notifications${copilotQuery({ limit, before })}`, { signal });
export const fetchCopilotNotificationsUnreadAPI = (signal) => copilotRequest("notifications/unread-count", { signal });
export const markCopilotNotificationReadAPI = (id) => copilotRequest(`notifications/${encodeURIComponent(id)}/read`, { method: "POST", body: {} });
// -> { preferences: { email, inApp } }
export const fetchCopilotPreferencesAPI = () => copilotRequest("preferences");
export const updateCopilotPreferencesAPI = (patch) => copilotRequest("preferences", { method: "PUT", body: patch });

// Exchanges the single-use token of an e-mailed guest link for a guest session
// of this browser -> { conversation, guestAccess: { token } }.
export const redeemCopilotEmailLinkAPI = (conversationId, token) =>
  copilotRequest(`conversations/${encodeURIComponent(conversationId)}/email-access`, { method: "POST", noAuth: true, body: { token } });
